import { CookiesStorage } from '@ltvco/refresh-lib';
import { AppConfig, HiddenSectionsProvider } from '@ltvco/refresh-lib/ctx';
import {
  useFamilyTreeReports,
  useFocusedFamilyTreeMember,
} from '@ltvco/refresh-lib/fmt';
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
  nameize,
  ValidReportTypes,
} from '@ltvco/refresh-lib/utils';
import {
  BurialInformation,
  DebugMenu,
  ObituaryDetails,
  Owner,
  PersonReport as PersonReportType,
  ReportFactory,
  ReportLoading,
  ReportNullState,
  ReportOptions,
  useReportMonitors,
  useSession,
  HideableSection,
} from '@ltvco/refresh-lib/v1';
import {
  EmailSection as EmailSectionV2,
  PhoneSection as PhoneSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  NeighborsSection as NeighborsSectionV2,
  RelativesSection as RelativesSectionV2,
  AssociatesSection as AssociatesSectionV2,
  EducationSection as EducationSectionV2,
  JobsSection as JobsSectionV2,
  PersonReportOverview,
  ReportRating as ReportRatingV2,
  NameAncestrySection,
  NotesSection as NotesSectionV2,
  MaritalRecordsSection as MaritalRecordsSectionV2,
  AssetsSection,
  CriminalOrTrafficSection as CriminalOrTrafficSectionV2,
  BankruptcySection as BankruptcySectionV2,
  CourtRecords,
  CourtRecordsLoadingScreen,
  RelatedReportsSection as RelatedReportsSectionV2,
  LicensesAndPermitsSection as LicensesAndPermitsSectionV2,
  UsernameSection as UsernameSectionV2,
  SocialSectionV2,
  NavLinkDataV2,
  RecordsFoundModal,
  getEmulatedUseQueryResult,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { REPORT_PROCESSORS, SEARCH_TYPES } from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';
import {
  GenericReportResponse,
  ProcessedPersonReportData,
  ReportProps,
} from 'components/ChatBV/types';
import { getPersonNavLinkDataV2 } from 'navLinkData/personNavLinkData';
import { formatFamilyTreeDataFromPersonReport } from 'utils/formatFamilyTreeData';
import { getClaimedReports } from 'utils/getClaimedReports';

export const PersonChatBvReport = ({ permalink, reportData }: ReportProps) => {
  const {
    session: { account },
  } = useSession();
  const currentUserInfo = account?.account?.user_info;
  const storage = new CookiesStorage();
  const navigate = useNavigate();
  const { trackEventGA4 } = useContext(AppConfig);
  const { sendPersonFollowUpMessage, messages } = useContext(ChatContext);
  const { navigateToFocusedMember } = useFocusedFamilyTreeMember();
  const { currentMonitors, reportIsMonitored } = useReportMonitors(permalink);
  const apiQueryResult = useReport(permalink, undefined, 'person', {
    enabled: !reportData,
  });
  const queryResult = reportData
    ? getEmulatedUseQueryResult(reportData, true, false, false)
    : apiQueryResult;

  const location = useLocation();
  const isOnReportLibrary = location.pathname.includes('chat/library');

  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();

  const isInSuggestedEmailVerification = localStorage.getItem(
    'emailVerificationSelected'
  );
  const customTimeDelay = isInSuggestedEmailVerification ? 500 : undefined;
  useScrollToSectionOnNavigate(hash, queryResult, customTimeDelay);

  const [showCriminalData, setShowCriminalData] = useState(false);
  const [isCourtRecordsLoading, setIsCourtRecordsLoading] = useState(false);

  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    userCode: currentUserInfo?.user_code,
    potentialOwnerIndex: 0,
    hideContactInfo: false,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });

  const [claimedReportPermalink, setClaimedReportPermalink] =
    useState<string>('');

  const [updateDate, setUpdateDate] = useState<string | undefined>();
  const [report, setReport] = useState<PersonReportType | null>(null);
  const [reportBvId, setReportBvId] = useState<string>('');
  const [showNewRecordsModal, setShowNewRecordsModal] = useState(false);
  const [reportOwner, setReportOwner] = useState<Owner | null>(null);
  const [familyTreeBvIds, setFamilyTreeBvIds] = useState<string[]>([]);
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1569');
  const alternativeTitle = isInPartialAddressesVariation
    ? 'Possible Locations'
    : undefined;

  const date = new DateUtil();

  const queryClient = useQueryClient();

  useEffect(() => {
    setReportOptions((prevOptions: ReportOptions) => ({
      ...prevOptions,
      userCode: currentUserInfo?.user_code,
    }));
  }, []);

  const { data: reportsData } = useFamilyTreeReports({
    bodyOptions: {
      report_type: ['PersonReport'],
      placeholders_excluded: true,
    },
    logNotFoundErrorEnabled: false,
  });

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);

      // The format of queryResult from REMO has an extra data key.
      if (queryResult?.data?.entities) {
        setReport(ReportFactory.create(queryResult?.data, 'person'));
      }
    }
    if (!isInPartialAddressesVariation) {
      storage.setValue('partialLocView', 'false');
    }
  }, [queryResult.data, queryResult.isSuccess]);

  useEffect(() => {
    const remoUpdateDate = currentMonitors?.find(
      (monitor) => monitor.permalink === permalink
    )?.updated_at;
    const coreUpdateDate = report?.data?.meta?.updated_at;

    if (reportIsMonitored && remoUpdateDate) {
      setUpdateDate(date.parseDateFromString(remoUpdateDate, 'yyyy-MM-dd'));
    } else if (coreUpdateDate) {
      setUpdateDate(
        date.parseDateFromString(
          coreUpdateDate,
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss ZZZ'
        )
      );
    }
  }, [
    report?.data?.meta?.updated_at,
    currentMonitors,
    permalink,
    reportIsMonitored,
  ]);

  useEffect(() => {
    if (!currentMonitors) {
      return;
    }

    const REPORT_TYPE = 'detailed_person_report';
    const claimedReports = getClaimedReports(currentMonitors, REPORT_TYPE);

    if (claimedReports.length) {
      const mainPermalink = claimedReports[0].permalink;
      setClaimedReportPermalink(mainPermalink);
    } else {
      if (claimedReportPermalink !== '') {
        setClaimedReportPermalink('');
      }
    }
  }, [currentMonitors, claimedReportPermalink]);

  useEffect(() => {
    if (report) {
      const tempReportOwner = report.getOwner(reportOptions);
      if (tempReportOwner) {
        setReportOwner(tempReportOwner);
      }

      const queryParameters =
        report?.data?.meta?.request_info?.query_parameters;

      if (queryParameters) {
        const bvidObj = queryParameters.find(
          (param: { name: string }) => param.name === 'bvid'
        );
        setReportBvId(bvidObj?.['values']?.[0] || '');
      }
    }
  }, [report, reportOptions]);

  useEffect(() => {
    if (queryResult?.data?.meta?.permalink && !isOnReportLibrary) {
      const processedReport = REPORT_PROCESSORS[
        SEARCH_TYPES.PERSON as keyof typeof REPORT_PROCESSORS
      ](queryResult.data as any, permalink);
      if (
        messages
          .map((message) => message.content)
          .includes(
            `Tell me about ${
              (processedReport as ProcessedPersonReportData).data.personName
            }`
          )
      ) {
        return;
      }
      sendPersonFollowUpMessage(
        (processedReport as ProcessedPersonReportData)?.data?.personName || '',
        {
          reportData: queryResult.data as GenericReportResponse,
          type: SEARCH_TYPES.PERSON,
          permalink,
        }
      );
    }
  }, [queryResult?.data?.meta?.permalink]);

  useEffect(() => {
    if (!reportsData) return;

    const bvIds = reportsData.reports
      .map((report) => ('bvid' in report ? report.bvid : null))
      .filter((bvid): bvid is string => bvid !== null);

    setFamilyTreeBvIds(bvIds || []);
  }, [reportsData]);

  const hideCriminalBJLData = account?.account.user_settings
    .hide_criminal_records as boolean;

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={14} />;
  }

  if (zeroed) {
    return <ReportNullState />;
  }

  const reportUpgraded = queryResult?.data?.meta?.report_upgraded;
  const revealFreeBonusData = showCriminalData && !isCourtRecordsLoading;

  if (!reportOwner || !report) {
    return <ReportLoading menuItems={14} />;
  }

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }

  const {
    ownerName,
    nameInfo,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    marital,
    relatives,
    associates,
    neighbors,
    courts,
    obituary,
    grave,
    bonusData,
  } = reportOwner as Owner;

  const personNavLinkDataV2 = getPersonNavLinkDataV2(
    reportOwner as Owner,
    hideCriminalBJLData,
    revealFreeBonusData,
    relatives || []
  );

  const dob =
    reportOwner.identity?.dobs?.[0]?.date?.parsed?.year?.toString() ?? '';
  const shouldShowObituarySection = !!obituary?.funeralHome;
  document.title = `${report.personName} - BeenVerified`;

  const reportInFamilyTree = reportsData?.reports?.find((report) => {
    return 'bvid' in report && report.bvid === reportBvId;
  });

  const handleAddingRelativeToFamilyTree = () => {
    trackEventGA4({
      event_name: 'report_element_engagement',
      report_type: 'people',
      type: 'add_family',
      interacted_from: 'overview',
    });

    // Find the person report's data to format.
    const person = report.data.people?.[0];
    if (!person) {
      return null;
    }

    if (reportInFamilyTree) {
      navigateToFocusedMember(reportInFamilyTree?.member_id as number);
      return;
    }

    localStorage.setItem(
      'familyTreeRelativeToAdd',
      JSON.stringify(
        formatFamilyTreeDataFromPersonReport(
          person,
          permalink,
          person.identity.ids[0] || '',
          addresses[0]?.parsed || {}
        )
      )
    );

    navigate(`/family-tree?from=${permalink}`);
  };

  const handleAddingPossibleRelativeToFamilyTree = (bvid: string) => {
    trackEventGA4({
      event_name: 'report_element_engagement',
      report_type: 'people',
      type: 'add_family',
      interacted_from: 'possible_relatives',
    });

    // Find the person report's relative's data to format.
    const relative = relatives?.find(
      (person) => person.identity.ids[0] === bvid
    );
    if (!relative) {
      return null;
    }

    localStorage.setItem(
      'familyTreeRelativeToAdd',
      JSON.stringify(
        formatFamilyTreeDataFromPersonReport(
          relative,
          null,
          bvid,
          relative.addresses[0]?.parsed || {}
        )
      )
    );

    navigate(`/family-tree?from=${permalink}`);
  };

  const delayForCourtRecords = 1000;

  function handleCourtRecordsLoadingScreen() {
    setIsCourtRecordsLoading(true);
    setShowNewRecordsModal(true);
    setTimeout(() => {
      setIsCourtRecordsLoading(false);
      setShowCriminalData(true);
    }, delayForCourtRecords * 7);
  }
  const redirectUrl = `${window.location}`;

  const recordsFound = [
    {
      label: 'Criminal or Traffic records',
      count: courts?.criminal?.length || 0,
    },
    { label: 'Bankruptcies', count: courts?.bankruptcy?.length || 0 },
    { label: 'Licenses & Permits', count: courts?.civil?.length || 0 },
  ]
    .sort((a, b) => Number(b.count > 0) - Number(a.count > 0))
    .map(({ label, count }) => `${count} ${label}`);

  return (
    <HiddenSectionsProvider reportType="detailed_person_report">
      <Box>
        <Stack direction="row">
          <Box marginTop={0}>
            <PersonReportOverview
              isConfirmed={showCriminalData}
              reportType={SEARCH_TYPES.PERSON as ValidReportTypes}
              owner={reportOwner as Owner}
              personName={ownerName}
              personOverviewItemsData={{
                hideCriminalBJLData,
                phones,
                emails,
                addresses,
                relatives,
                images: reportOwner!.images,
                profiles,
                usernames,
                jobs,
                educations,
              }}
              dateUpdated={updateDate}
              permalink={permalink}
              navLinkData={personNavLinkDataV2 as unknown as NavLinkDataV2[]}
              reportUpgraded={reportUpgraded}
              familyTreeCTA={false}
              reportInFamilyTree={!!reportInFamilyTree}
              addRelativeToFamilyTree={handleAddingRelativeToFamilyTree}
            />

            <HideableSection
              nameToDisplay="Possible Phone Numbers"
              nameToRecord="possible_phone_numbers"
              id="phone-numbers-section"
            >
              <PhoneSectionV2
                personName={ownerName}
                phoneList={phones}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Email Addresses"
              nameToRecord="possible_email_addresses"
              id="email-section"
            >
              <EmailSectionV2
                personName={ownerName}
                emailList={emails}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Address History"
              nameToRecord="possible_address_history"
              id="address-history-section"
            >
              <AddressHistorySectionV2
                addresses={addresses}
                personName={ownerName}
                permalink={permalink}
                realEstate={ownedAssets?.real_estate}
                alternateTitle={alternativeTitle}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Social Media"
              nameToRecord="possible_social_media"
              id="possible-social-media"
            >
              <SocialSectionV2
                personName={ownerName}
                profiles={profiles}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Usernames"
              nameToRecord="usernames-section"
              id="possible-usernames"
            >
              <UsernameSectionV2 personName={ownerName} usernames={usernames} />
            </HideableSection>

            <HideableSection
              nameToDisplay="Names & Ancestry"
              nameToRecord="names_and_ancestry"
              id="names-ancestry-section"
            >
              <NameAncestrySection
                firstName={nameize(nameInfo?.parsed?.first ?? null)}
                surName={nameize(nameInfo?.parsed?.last ?? null)}
                yearOfBirth={dob}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Relatives"
              nameToRecord="possible_relatives"
              id="relatives-section"
            >
              <RelativesSectionV2
                personName={ownerName}
                relatives={relatives || []}
                permalink={permalink}
                familyTreeBvIds={familyTreeBvIds}
                addRelativeToFamilyTree={
                  handleAddingPossibleRelativeToFamilyTree
                }
              />
            </HideableSection>

            <>
              {shouldShowObituarySection && (
                <ObituaryDetails obituary={obituary} permalink={permalink} />
              )}
              {grave && (
                <BurialInformation grave={grave} permalink={permalink} />
              )}
            </>

            <HideableSection
              nameToDisplay="Possible Marriage & Divorce Records"
              nameToRecord="possible_marriage_divorce_records"
              id="marital-records-section"
            >
              <MaritalRecordsSectionV2
                personName={ownerName}
                marital={marital}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Associates"
              nameToRecord="possible_associates"
              id="associates-section"
            >
              <AssociatesSectionV2
                personName={ownerName}
                associates={associates || []}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Neighbors"
              nameToRecord="possible_neighbors"
              id="neighbors-section"
            >
              <NeighborsSectionV2
                personName={ownerName}
                neighbors={neighbors || []}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Court Orders"
              nameToRecord="court_orders"
              id="criminal-or-traffic-section"
            >
              {!hideCriminalBJLData && (
                <>
                  {revealFreeBonusData ? (
                    <>
                      <CriminalOrTrafficSectionV2
                        criminalOrTrafficData={courts.criminal || []}
                        personName={ownerName}
                      />
                      <BankruptcySectionV2
                        personName={ownerName}
                        bankruptcyData={courts.bankruptcy || []}
                      />
                      <LicensesAndPermitsSectionV2
                        personName={ownerName}
                        bonusData={bonusData}
                      />
                    </>
                  ) : (
                    <>
                      {isCourtRecordsLoading ? (
                        <CourtRecordsLoadingScreen
                          personName={ownerName}
                          delayDuration={delayForCourtRecords}
                        />
                      ) : (
                        <CourtRecords
                          handleCourtRecordsLoadingScreen={
                            handleCourtRecordsLoadingScreen
                          }
                          redirectUrl={redirectUrl}
                        />
                      )}
                    </>
                  )}
                </>
              )}

              <RecordsFoundModal
                open={showNewRecordsModal}
                handleClose={() => setShowNewRecordsModal(false)}
                recordsFound={recordsFound}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Jobs"
              nameToRecord="possible_jobs"
              id="jobs-section"
            >
              <JobsSectionV2
                personName={ownerName}
                jobs={jobs}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Education"
              nameToRecord="possible_education"
              id="education-section"
            >
              <EducationSectionV2
                personName={ownerName}
                educations={educations}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Owned Assets"
              nameToRecord="possible_owned_assets"
              id="possible-owned-assets-section"
            >
              <AssetsSection
                personName={ownerName}
                ownedAssets={ownedAssets}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="notes"
              nameToRecord="notes"
              id="Notes-section"
            >
              <NotesSectionV2 permalink={permalink} />
            </HideableSection>

            <HideableSection
              nameToDisplay="Related Reports"
              nameToRecord="related_reports"
              id="related-reports-section"
            >
              <RelatedReportsSectionV2
                reportType="person"
                personName={ownerName}
                potentialOwners={report.data.people}
                relatives={relatives}
                addresses={addresses}
                phones={phones}
                emails={emails}
                usernames={usernames}
              />
            </HideableSection>

            <ReportRatingV2
              rating={report.data.meta?.rating || null}
              report_type={SEARCH_TYPES.PERSON}
            />
          </Box>
        </Stack>
        <DebugMenu menuItems={report.data.rawData.debug_menu} />
      </Box>
    </HiddenSectionsProvider>
  );
};
